// DMS document-type registry — FRONTEND display mirror of
// netlify/functions/_shared/dms-types.js. Same `key` strings (the contract);
// this side owns labels, grouping, help text and the structured-form field lists.
// Keep the keys + kinds in sync with the backend registry.

export const DMS_GROUPS = ['Identity', 'Licences & Work', 'Vehicle', 'Employment', 'Business & Personal'];

export const DMS_TYPES = [
  { key: 'drivers_licence', label: "Australian Driver's Licence", group: 'Identity',
    kind: 'file', expiry: true, numberLabel: 'Licence number',
    // Top-level Front/Back: used by the office uploader (CompliancePage AdminFileEditor)
    // to prompt & retain both sides of a plastic card. The driver portal has variants
    // (below) and ignores this, so the two flows don't collide.
    sides: ['Front', 'Back'],
    // Both a plastic card and the Service NSW digital licence carry every field we
    // need — but they're in different places, so we ask which one they have and
    // guide the two captures accordingly. On the digital licence the card number is
    // at the BOTTOM of the screen, below the fold: a single screenshot misses it.
    variants: [
      { key: 'physical', label: 'Plastic card', sides: ['Front', 'Back'],
        help: 'Photograph both sides of your licence card.' },
      { key: 'digital', label: 'Digital licence (Service NSW app)',
        sides: ['Top of screen', 'Bottom of screen'],
        help: 'Open your licence in the Service NSW app and take two screenshots: the top (licence number + expiry), then scroll down for the bottom (card number).' },
    ],
    // The card number is mandatory to verify a NSW licence against the government
    // record (DVS), and changes with every reissue. `rearAddress` records a
    // change-of-address shown on the back (uncommon) — office-only (adminOnly), so it
    // doesn't clutter the driver's own upload form.
    extraFields: [
      { key: 'cardNumber', label: 'Card number', placeholder: 'Changes each time a card is issued' },
      { key: 'rearAddress', label: 'Address change (rear)', placeholder: 'Only if the back shows an updated address', adminOnly: true },
    ],
    help: 'Your licence — plastic card or the digital licence in the Service NSW app.' },

  { key: 'passport', label: 'Passport', group: 'Identity',
    kind: 'file', expiry: true, numberLabel: 'Passport number',
    help: 'Photograph the photo/data page.' },

  // An older passport a visa is still linked to — the vSure/VEVO check uses it instead
  // of the current passport while it's on file. Office-only; mirrors the backend.
  { key: 'visa_passport', label: 'Passport the Visa Is On', group: 'Identity',
    kind: 'file', expiry: false, optional: true, numberLabel: 'Passport number',
    help: 'Only when the visa was granted on an older passport that Home Affairs hasn’t updated — the VEVO check runs on this one. Remove it once the visa is linked to the current passport.' },

  { key: 'visa', label: 'Visa Grant Notice', group: 'Identity',
    kind: 'file', expiry: true, optional: true, numberLabel: 'Visa grant number',
    help: 'Only if applicable — your official visa grant notice (the letter from Home Affairs).' },

  // Student pack — only required for people whose visa is a Student visa (added to
  // their required set automatically; hidden for everyone else).
  { key: 'visa_coe', label: 'Confirmation of Enrolment (CoE)', group: 'Identity',
    kind: 'file', expiry: false, optional: true, numberLabel: 'CoE number',
    help: 'The Confirmation of Enrolment (the CRICOS-registered CoE from the provider) — your own if you’re the student, or the primary holder’s if you’re a partner / dependent.' },
  { key: 'visa_loe', label: 'Letter of Enrolment (LoE)', group: 'Identity',
    kind: 'file', expiry: false, optional: true,
    help: 'The Letter of Enrolment confirming current enrolment — your own if you’re the student, or the primary holder’s if you’re a partner / dependent.' },
  { key: 'visa_acceptance', label: 'College Acceptance Letter', group: 'Identity',
    kind: 'file', expiry: false, optional: true,
    help: 'Students only — the acceptance / offer letter from your college or university.' },
  { key: 'visa_timetable', label: 'Study Timetable', group: 'Identity',
    kind: 'file', expiry: false, optional: true,
    help: 'Students only — your current study timetable.' },

  { key: 'birth_certificate', label: 'Birth Certificate', group: 'Identity',
    // Conditional: required only when an Australian passport can't be produced — so
    // NOT in the default required set (optional); the office ticks it when needed.
    kind: 'file', expiry: false, optional: true, numberLabel: 'Registration number',
    help: 'Only needed if you can’t provide an Australian passport. A clear scan or photo.' },

  { key: 'citizenship', label: 'Australian Citizenship Document', group: 'Identity',
    // Front + back — the REAR carries two numbers: the ACC number (filed as the
    // document number) and the Evidence number (captured alongside). Conditional:
    // required only when neither an Australian passport nor a birth certificate can be
    // produced, or when someone becomes an Australian citizen.
    kind: 'file', expiry: false, optional: true, numberLabel: 'ACC number',
    sides: ['Front', 'Back'],
    extraFields: [
      { key: 'evidenceNumber', label: 'Evidence number', placeholder: 'Also on the back of the certificate' },
    ],
    help: 'Only if you can’t provide an Australian passport or birth certificate. Photograph the front and the back (the numbers are on the back).' },

  { key: 'ap_id_card', label: 'Australia Post ID Card', group: 'Licences & Work',
    kind: 'file', expiry: true, numberLabel: 'Card / ID number',
    help: 'Your Australia Post photo ID / access card.' },

  { key: 'workers_comp', label: 'Workers Compensation Policy', group: 'Licences & Work',
    kind: 'file', expiry: true, numberLabel: 'Policy number',
    help: 'Your workers compensation certificate of currency.' },

  { key: 'scanner_asset', label: 'Scanner Asset Numbers', group: 'Licences & Work',
    kind: 'fields', expiry: false,
    help: 'The asset number(s) on your Australia Post handheld device.',
    fields: [{ key: 'assetNumber', label: 'AP handheld asset number(s)', placeholder: 'e.g. 100482, 100931' }] },

  { key: 'vehicle_insurance', label: 'Vehicle Insurance', group: 'Vehicle',
    kind: 'file', expiry: true, numberLabel: 'Policy number',
    help: 'Your vehicle insurance certificate of currency.' },

  { key: 'vehicle_rego', label: 'Vehicle Registration', group: 'Vehicle',
    kind: 'file', expiry: true, numberLabel: 'Rego / plate',
    // Verified ONLINE against NEVDIS (Blue Flag) for the assigned Fleet vehicle — the
    // live registration check is the proof, so no document is uploaded/needed.
    onlineCheck: true,
    help: 'Verified online from the assigned vehicle’s registration — no document needed.' },

  { key: 'company_details', label: 'Company Details', group: 'Business & Personal',
    kind: 'fields', expiry: false,
    help: 'Your business/company registration details.',
    fields: [
      { key: 'businessName', label: 'Business name', placeholder: 'Registered business name' },
      { key: 'acn', label: 'ACN', placeholder: 'Australian Company Number' },
      { key: 'abn', label: 'ABN', placeholder: 'Australian Business Number' },
    ] },

  { key: 'primary_address', label: 'Primary Address', group: 'Business & Personal',
    kind: 'fields', expiry: false,
    help: 'Your current residential address.',
    fields: [{ key: 'address', label: 'Residential address', placeholder: 'Unit / street / suburb / state / postcode' }] },

  { key: 'emergency', label: 'Emergency Contacts', group: 'Business & Personal',
    kind: 'fields', expiry: false,
    help: 'Who we should call in an emergency.',
    fields: [
      { key: 'contact1Name', label: 'Contact 1 — name' },
      { key: 'contact1Rel', label: 'Contact 1 — relationship' },
      { key: 'contact1Phone', label: 'Contact 1 — phone' },
      { key: 'contact2Name', label: 'Contact 2 — name (optional)' },
      { key: 'contact2Rel', label: 'Contact 2 — relationship (optional)' },
      { key: 'contact2Phone', label: 'Contact 2 — phone (optional)' },
    ] },

  // ── night-sorter (PAYG employee) pack ───────────────────────────────────────
  { key: 'tfn_declaration', label: 'TFN Declaration', group: 'Employment',
    kind: 'file', expiry: false,
    // noNumber: the only number on this form IS the tax file number — a secret, not
    // a document reference. It stays encrypted in the sorter vault; this is the form.
    noNumber: true,
    // captureTfn: OCR reads the TFN off the form so it can be confirmed and stored
    // in the encrypted vault (never in the document store). See MyDmsPortalTab /
    // CompliancePage AdminFileEditor for the confirm field.
    captureTfn: true,
    // The rest of the payee's Section A answers (everything except the TFN) — read
    // off the form and kept on the document. Mirrors the backend registry.
    extraFields: [
      { key: 'payeeName', label: 'Name (on declaration)', placeholder: 'Title, given & family name' },
      { key: 'homeAddress', label: 'Home address', placeholder: 'Street, suburb, state, postcode' },
      { key: 'dob', label: 'Date of birth', placeholder: 'YYYY-MM-DD' },
      { key: 'email', label: 'Email', placeholder: 'From the email question' },
      { key: 'basisOfPayment', label: 'Basis of payment', placeholder: 'Full-time / Part-time / Casual / Labour hire' },
      { key: 'residencyStatus', label: 'Residency for tax', placeholder: 'Australian resident / Foreign resident / WHM' },
      { key: 'taxFreeThreshold', label: 'Claims tax-free threshold', placeholder: 'yes / no' },
      { key: 'stslDebt', label: 'HELP/STSL debt', placeholder: 'yes / no' },
      { key: 'signatureDate', label: 'Signed date', placeholder: 'YYYY-MM-DD' },
    ],
    help: 'Your signed Tax File Number declaration. We read the TFN (stored securely) plus the rest of your declaration off the form — check the details before saving.' },

  { key: 'super_choice', label: 'Superannuation Standard Choice Form', group: 'Employment',
    kind: 'file', expiry: false,
    // noNumber: the generic read returns the ATO's own "NAT 13080-…" publication
    // number, which is meaningless. The fund details below are what payroll needs —
    // they mirror across to sorters.super_fund / super_usi / super_member_no on save.
    noNumber: true,
    // captureTfn: Section A of this form carries the TFN → routed to the vault.
    captureTfn: true,
    extraFields: [
      { key: 'fullName', label: 'Full name (Section A)', placeholder: 'Employee name' },
      { key: 'employeeNumber', label: 'Employee number', placeholder: 'Payroll/employee number' },
      { key: 'superChoice', label: 'Super choice', placeholder: 'Existing fund / Employer default / SMSF' },
      { key: 'fundName', label: 'Super fund name', placeholder: 'e.g. Mercer Smartsuper' },
      { key: 'fundAbn', label: 'Fund ABN', placeholder: 'The FUND’s ABN, not ours' },
      { key: 'usi', label: 'USI', placeholder: 'Unique Superannuation Identifier' },
      { key: 'memberNumber', label: 'Member account number', placeholder: 'Their account number in the fund' },
      { key: 'accountName', label: 'Name on the super account', placeholder: 'Exactly as it appears on the account' },
      { key: 'letterOfCompliance', label: 'Letter of compliance attached', placeholder: 'yes / no' },
      { key: 'signatureDate', label: 'Signed date', placeholder: 'YYYY-MM-DD' },
    ],
    help: 'Your signed super choice form. We read the fund name, USI and member number (so super is paid to the right account) plus the rest of the form — check the details before saving.' },

  { key: 'fair_work_ack', label: 'Fair Work Statements Issued', group: 'Employment',
    kind: 'fields', expiry: false,
    help: 'Records the date we gave you the Fair Work Information Statement and, for casuals, the Casual Employment Information Statement.',
    fields: [
      { key: 'fwisDate', label: 'Fair Work Information Statement — date given', placeholder: 'YYYY-MM-DD' },
      { key: 'casualDate', label: 'Casual Employment Information Statement — date given', placeholder: 'YYYY-MM-DD' },
    ] },
].filter((t) => !t.hidden);

export const DMS_TYPE_BY_KEY = Object.fromEntries(DMS_TYPES.map((t) => [t.key, t]));

// Which population each type applies to — mirrors TYPE_AUDIENCE in the backend
// registry (netlify/functions/_shared/dms-types.js). Drivers are subcontractors,
// night sorters are PAYG employees; identity documents are common to both.
// Unlisted keys fall back to driver-only, matching the backend.
export const DMS_TYPE_AUDIENCE = {
  drivers_licence: ['driver'], vehicle_insurance: ['driver'], vehicle_rego: ['driver'],
  workers_comp: ['driver'], company_details: ['driver'], scanner_asset: ['driver'],
  tfn_declaration: ['sorter'], super_choice: ['sorter'], fair_work_ack: ['sorter'],
  ap_id_card: ['driver', 'sorter'], passport: ['driver', 'sorter'], visa: ['driver', 'sorter'], visa_passport: ['driver', 'sorter'],
  visa_coe: ['driver', 'sorter'], visa_loe: ['driver', 'sorter'], visa_acceptance: ['driver', 'sorter'], visa_timetable: ['driver', 'sorter'],
  birth_certificate: ['driver', 'sorter'], citizenship: ['driver', 'sorter'],
  emergency: ['driver', 'sorter'], primary_address: ['driver', 'sorter'],
};
// Mirrors isStudentVisa in driver-dms-sb.js: filed as Student, or VEVO says subclass 500
// held as the primary applicant.
export const isStudentVisa = (fields) => !!(fields && ((fields.visaDetails && fields.visaDetails.visaTypeLabel === 'Student')
  || (fields.vevo && String(fields.vevo.subclass || '') === '500' && fields.vevo.applicant !== 'secondary')));
export const dmsAppliesTo = (key, audience) => (DMS_TYPE_AUDIENCE[key] || ['driver']).includes(audience);
export const dmsTypesFor = (audience) => DMS_TYPES.filter((t) => dmsAppliesTo(t.key, audience));

// How a document reached us. A screenshot of a digital licence defeats the app's
// anti-fraud features (moving hologram, "last refreshed" time, expiring QR), and
// Service NSW advises against accepting one — so it's accepted for convenience but
// shown as low-assurance rather than treated as proof.
export const SOURCE_LABEL = {
  physical_card:     { label: 'Physical card', color: 'var(--hp-green)', note: '' },
  digital_screenshot: { label: 'Screenshot — unverified', color: '#b8860b',
    note: 'This is a screenshot of a digital licence, so it isn’t proof on its own. The office may ask to see the physical card.' },
  unknown:           { label: '', color: 'var(--hp-dim)', note: '' },
};

// Status → colour + label, shared by the driver tab and the admin dashboard.
export const DMS_STATUS = {
  ok:       { label: 'On file',     color: 'var(--hp-green)',  bg: 'rgba(46,125,50,0.10)' },
  expiring: { label: 'Expiring',    color: '#b8860b',          bg: 'rgba(201,168,76,0.14)' },
  expired:  { label: 'Expired',     color: 'var(--hp-red)',    bg: 'rgba(214,69,69,0.10)' },
  missing:  { label: 'Not on file', color: 'var(--hp-dim)',    bg: 'transparent' },
  renewal:  { label: 'Renewal requested', color: '#3b82f6',    bg: 'rgba(59,130,246,0.14)' },
};
